import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { departmentsApi, employees, positionsApi, staffTransfers } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import PrintActions from '@/shared/components/PrintActions';
import { partyCodesText } from '@/shared/utils/partyCodes';

const fmt2 = (v: string | number | null) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function StaffTransferPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['staff-transfer', id ?? ''],
    queryFn: () => staffTransfers.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const empQ = useQuery({
    queryKey: ['employee-full', docQ.data?.employee_id ?? ''],
    queryFn: () => employees.get(docQ.data!.employee_id),
    enabled: !!docQ.data,
  });
  const stateAtQ = useQuery({
    queryKey: ['employee-state-at', docQ.data?.employee_id, docQ.data?.transfer_date],
    queryFn: () => {
      // Стан безпосередньо перед переміщенням.
      const before = new Date(docQ.data!.transfer_date);
      before.setDate(before.getDate() - 1);
      return employees.stateAt(docQ.data!.employee_id, before.toISOString().slice(0, 10));
    },
    enabled: !!docQ.data,
  });
  const posQ = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list({ limit: 500 }) });
  const depQ = useQuery({
    queryKey: ['departments', docQ.data?.organization_id ?? ''],
    queryFn: () => departmentsApi.list({ organization_id: docQ.data!.organization_id, limit: 500 }),
    enabled: !!docQ.data,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!docQ.data || !empQ.data) return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  const d = docQ.data;
  const org = orgsQ.data?.items.find((o) => o.id === d.organization_id);
  const posName = (pid: string | null | undefined) =>
    pid ? posQ.data?.items.find((p) => p.id === pid)?.name ?? '—' : '—';
  const depName = (did: string | null | undefined) =>
    did ? depQ.data?.items.find((p) => p.id === did)?.name ?? '—' : '—';

  const fromState = stateAtQ.data;

  return (
    <div className="print-page" style={{
      padding: '15mm', fontFamily: 'Times, serif', fontSize: '10pt',
      maxWidth: '210mm', margin: '0 auto', background: 'white', lineHeight: 1.4,
    }}>
      <PrintActions docMeta={{
        organizationId: d.organization_id,
        documentType: 'doc.staff_transfers',
        documentId: d.id,
        filename: `Наказ про переведення Т-5 ${d.document_number ?? ''}`.trim(),
        orgName: org?.name ?? null,
      }} />

      <div style={{ textAlign: 'center' }}>
        <div>{org?.full_name ?? org?.name ?? ''}</div>
        {partyCodesText(org) && <div style={{ fontSize: '9pt' }}>{partyCodesText(org)}</div>}
      </div>

      <h2 style={{ textAlign: 'center', fontSize: '14pt', margin: '1.5rem 0 0.3rem' }}>
        НАКАЗ № {d.document_number}
      </h2>
      <p style={{ textAlign: 'center', margin: 0 }}>
        від {new Date(d.document_date).toLocaleDateString('uk-UA')}
      </p>
      <p style={{ textAlign: 'center', margin: '0.5rem 0', fontWeight: 600 }}>
        про переведення працівника на іншу роботу
      </p>

      <p style={{ marginTop: '1.5rem' }}>
        Перевести <strong>{empQ.data.full_name}</strong>
        {empQ.data.code ? ` (таб. № ${empQ.data.code})` : ''} з&nbsp;
        <strong>{new Date(d.transfer_date).toLocaleDateString('uk-UA')}</strong>:
      </p>

      <table style={{ width: '100%', marginTop: '1rem', borderCollapse: 'collapse', border: '1px solid #000' }}>
        <thead>
          <tr style={{ background: '#f8fafc' }}>
            <th style={{ padding: '0.4rem', border: '1px solid #000', width: '40%' }}></th>
            <th style={{ padding: '0.4rem', border: '1px solid #000' }}>Попередня робота</th>
            <th style={{ padding: '0.4rem', border: '1px solid #000' }}>Нова робота</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}><strong>Підрозділ:</strong></td>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              {depName(fromState?.department_id)}
            </td>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              {depName(d.new_department_id ?? fromState?.department_id)}
            </td>
          </tr>
          <tr>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}><strong>Посада:</strong></td>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              {posName(fromState?.position_id)}
            </td>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}>
              {posName(d.new_position_id ?? fromState?.position_id)}
            </td>
          </tr>
          <tr>
            <td style={{ padding: '0.4rem', border: '1px solid #000' }}><strong>Оклад, грн:</strong></td>
            <td style={{ padding: '0.4rem', border: '1px solid #000', textAlign: 'right' }}>
              {fmt2(fromState?.salary_amount ?? null)}
            </td>
            <td style={{ padding: '0.4rem', border: '1px solid #000', textAlign: 'right' }}>
              {Number(d.new_salary_amount) > 0 ? fmt2(d.new_salary_amount) :
               fmt2(fromState?.salary_amount ?? null)}
            </td>
          </tr>
        </tbody>
      </table>

      {d.reason && (
        <p style={{ marginTop: '1rem' }}>
          <strong>Причина переведення:</strong> {d.reason}
        </p>
      )}

      <table style={{ width: '100%', marginTop: '3rem' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%' }}>
              <div>Керівник:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>(підпис, П.І.Б.)</div>
            </td>
            <td style={{ width: '50%' }}>
              <div>З наказом ознайомлений:</div>
              <div style={{ marginTop: '2rem', borderTop: '1px solid #000', textAlign: 'center', width: '80%' }}>
                ___________________
              </div>
              <div style={{ fontSize: '8pt', textAlign: 'center', width: '80%' }}>
                (підпис працівника, дата)
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <style>{`
        @media print {
          .print-hide { display: none !important; }
        }
      `}</style>
    </div>
  );
}
